import React, { useEffect, useState } from 'react';
import { getProfile, submitPhiAttestation } from '../api/profile';
import { PHI_ATTESTATION_VERSION } from '../consent/versions';

function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Blocks wizard data entry until HC-17 PHI attestation is recorded.
export default function PhiAttestationGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [required, setRequired] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const profile = await getProfile();
        if (cancelled) return;
        setRequired(Boolean(profile.phi_attestation_required));
        setAccepted(!profile.phi_attestation_required);
      } catch {
        if (!cancelled) {
          setRequired(true);
          setAccepted(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAccept() {
    if (!checked) {
      setError('Please confirm the attestation to continue.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await submitPhiAttestation({
        attestationVersion: PHI_ATTESTATION_VERSION,
        calculatorSessionId: newSessionId(),
      });
      setAccepted(true);
      setRequired(false);
    } catch (err) {
      setError(err?.code || err?.message || 'phi_attestation_failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return null;
  }

  if (accepted || !required) {
    return children;
  }

  return (
    <div style={gateWrapStyle} data-testid="phi-attestation-gate">
        <div style={modalStyle} role="dialog" aria-modal="true" aria-labelledby="phi-attestation-title">
          <h2 id="phi-attestation-title" style={titleStyle}>Before you enter valuation inputs</h2>
          <p style={ledeStyle}>
            This attestation applies to data you type into the calculator — not your account registration.
          </p>
          <label style={checkRowStyle}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="phi-attestation-checkbox"
            />
            <span>
              I confirm that the information I enter into this tool does not include protected health
              information (PHI) or other patient-identifiable data, unless expressly permitted under a
              separate agreement with LooporaData.
            </span>
          </label>
          {error && <p style={errorStyle}>{error}</p>}
          <button
            type="button"
            style={submitStyle}
            disabled={submitting}
            onClick={handleAccept}
            data-testid="phi-attestation-continue"
          >
            {submitting ? 'Saving…' : 'Continue to calculator inputs'}
          </button>
        </div>
    </div>
  );
}

const gateWrapStyle = {
  minHeight: '240px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const modalStyle = {
  maxWidth: '520px',
  width: '100%',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: '8px',
  padding: '28px 32px',
  boxShadow: 'var(--shadow-sm)',
};

const titleStyle = {
  fontFamily: 'var(--display)',
  fontSize: '22px',
  fontWeight: 500,
  margin: 0,
  color: 'var(--ink)',
};

const ledeStyle = { fontSize: '13px', color: 'var(--ink2)', lineHeight: 1.55, margin: '12px 0 0' };
const checkRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  fontSize: '13px',
  color: 'var(--ink2)',
  marginTop: '20px',
};
const errorStyle = { margin: '12px 0 0', fontSize: '13px', color: 'var(--error)' };
const submitStyle = {
  height: '44px',
  marginTop: '20px',
  width: '100%',
  border: 'none',
  borderRadius: '999px',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
